import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import { Form, notification, Spin } from 'antd';

import AuditLogViewer from '@src/components/AuditLogViewer';
import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import CircleButton from '@src/components/Buttons/CircleButton';
import FilterContainer from '@src/components/FilterContainer';
import { FSCollections } from '@src/constants/database';
import { ALLOWED_ROLES } from '@src/packages/utils';

import WorkJourneyState from '../../components/WorkJourneyState';
import { WORK_JOURNEY_STATUS } from '../../constants';
import { useWorkJourneyContext } from '../../contexts';
import { useWorkJourneyById } from '../../hooks';
import {
  notifyWorkJourneyValidationErrors,
  runWorkJourneyValidation,
} from '../../utils/submitWorkJourneyForm';
import WorkJourneyActivateConfirm from './components/WorkJourneyActivateConfirm';
import WorkJourneyApplyChangesConfirm from './components/WorkJourneyApplyChangesConfirm';
import WorkJourneyDeactivateConfirm from './components/WorkJourneyDeactivateConfirm';
import WorkJourneyEditConfirm from './components/WorkJourneyEditConfirm';
import WorkJourneyEditForm from './components/WorkJourneyEditForm';
import WorkJourneyReturnConfirm from './components/WorkJourneyReturnConfirm';
import styles from './styles.module.less';

/**
 * @description View to edit an existing work journey
 * @returns
 */
function WorkJourneyEdit() {
  const history = useHistory();
  const intl = useIntl();
  const [form] = Form.useForm();
  const { id, orgId } = useParams<{ orgId: string; id: string }>();
  const {
    workJourneyType,
    loading,
    setWorkJourneyForm,
    toggleModalVisibility,
    hasUnsavedChanges,
    setInitialFormState,
    workJourneyForm,
  } = useWorkJourneyContext();

  const {
    data: workJourneyData,
    loading: loadingWorkJourney,
    error: workJourneyError,
  } = useWorkJourneyById(orgId, id);

  const isInitialized = useRef(false);
  const subcollectionsInitialized = useRef(false);

  useEffect(() => {
    if (workJourneyError) {
      notification.error({
        message: intl.formatMessage({ id: 'workJourney.errorLoading' }),
        description: (workJourneyError as Error).message,
      });
    }

    if (workJourneyData && !isInitialized.current) {
      const formData = { ...workJourneyData, organizationId: orgId };
      setWorkJourneyForm(formData);
      form.setFieldsValue(formData);
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workJourneyData?.id, workJourneyError]);

  // Wait for subcollections to load before setting initial state
  useEffect(() => {
    if (
      workJourneyData &&
      isInitialized.current &&
      !subcollectionsInitialized.current &&
      (workJourneyData.members?.length > 0 || workJourneyData.supervisors?.length > 0)
    ) {
      setWorkJourneyForm(prev => ({
        ...prev,
        organizationId: orgId,
        members: workJourneyData.members,
        supervisors: workJourneyData.supervisors,
      }));
      setInitialFormState({
        ...workJourneyData,
        organizationId: orgId,
        members: workJourneyData.members,
        supervisors: workJourneyData.supervisors,
      });
      subcollectionsInitialized.current = true;
    }
    // Fallback: if no subcollections after 800ms, set initial state anyway
    else if (workJourneyData && isInitialized.current && !subcollectionsInitialized.current) {
      const timer = setTimeout(() => {
        if (!subcollectionsInitialized.current) {
          setInitialFormState({ ...workJourneyData, organizationId: orgId });
          subcollectionsInitialized.current = true;
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workJourneyData]);

  const getTitle = () => {
    if (workJourneyData) return workJourneyData.name;
    if (workJourneyType) return intl.formatMessage({ id: 'workJourney.createJourney' });
    return intl.formatMessage({ id: 'workJourney.whatTypeCreate' });
  };

  const handleBack = () => {
    const isUpdated = workJourneyData && workJourneyData.state === WORK_JOURNEY_STATUS.UPDATED;
    if (isUpdated) toggleModalVisibility('returnModal');
    else history.go(-1);
  };

  const disableApplyChangesButton =
    workJourneyData && workJourneyData.state !== WORK_JOURNEY_STATUS.UPDATED;

  const openValidatedModal = (modal: 'updateModal' | 'applyChangesModal') => {
    const validation = runWorkJourneyValidation(workJourneyForm, intl);
    if (!validation.isValid) {
      notifyWorkJourneyValidationErrors(validation.errors, intl);
      return;
    }

    toggleModalVisibility(modal);
  };

  if (loading || loadingWorkJourney) {
    return (
      <BoxContainer>
        <BoxContainer content>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </BoxContainer>
      </BoxContainer>
    );
  }

  return (
    <BoxContainer>
      <BoxContainer content fixed bottomBorder>
        <FilterContainer
          className="w-100"
          goBack={handleBack}
          title={
            <div className={styles.titleContainer}>
              <Title.Header value={getTitle()} />
              <WorkJourneyState workJourney={workJourneyData} />
            </div>
          }
          actionButtons={[
            {
              allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
              iconName: 'save',
              type: 'primary',
              label: intl.formatMessage({ id: 'workJourney.edit.save' }),
              disabled: !hasUnsavedChanges,
              action: () => openValidatedModal('updateModal'),
            },
            {
              allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
              iconName: 'check_circle',
              type: 'default',
              label: intl.formatMessage({ id: 'workJourney.edit.apply' }),
              disabled: disableApplyChangesButton,
              action: () => openValidatedModal('applyChangesModal'),
            },
          ]}
          extraActionContent={
            <AuditLogViewer
              entityType={FSCollections.WORK_JOURNEY_CONFIGS}
              entityId={workJourneyData.id}
              recordLabel={workJourneyData.name || workJourneyData.id}
              triggerButton={
                <CircleButton
                  className="gx-ml-1"
                  iconName="schedule"
                  onClick={() => toggleModalVisibility('applyChangesModal')}
                  disabled={false}
                  hidden={false}
                  type="secondary"
                  tooltipText={intl.formatMessage({ id: 'auditLogViewer.title' })}
                />
              }
            />
          }
        />
      </BoxContainer>
      <BoxContainer content>
        <WorkJourneyEditForm form={form} />
      </BoxContainer>
      {/* Modal to back to the list of work journeys */}
      <WorkJourneyReturnConfirm />
      {/* Modal to edit the work journey */}
      <WorkJourneyEditConfirm />
      {/* Modal to apply changes */}
      <WorkJourneyApplyChangesConfirm />
      {/* Modal to activate the work journey */}
      <WorkJourneyActivateConfirm />
      {/* Modal to deactivate the work journey */}
      <WorkJourneyDeactivateConfirm />
    </BoxContainer>
  );
}

export default WorkJourneyEdit;
